import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardForm,
  CardHeader,
  CardTitle,
} from "@trillion/ui/components/ui/card";
import { Combobox, type ComboboxOption } from "../components/combobox";
import {
  passportApi,
  type GeoCountry,
  type GeoState,
} from "../lib/passport-api";

interface FormState {
  first_name: string;
  last_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
}

const EMPTY: FormState = {
  first_name: "",
  last_name: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
};

const clean = (v: string): string | null => (v.trim() === "" ? null : v.trim());

type Phase = "validating" | "invalid" | "ready";

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [phase, setPhase] = useState<Phase>("validating");
  const [email, setEmail] = useState<string>("");
  const [form, setForm] = useState<FormState>(EMPTY);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [states, setStates] = useState<GeoState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const latestCountry = useRef<string>("");

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }
    let active = true;
    (async () => {
      // Validate the invite first — ONLY a validate failure (or an invalid token) marks the invite invalid.
      try {
        const result = await passportApi.auth.validateInvite(token);
        if (!active) return;
        if (!result.valid) {
          setPhase("invalid");
          return;
        }
        setEmail(result.email ?? "");
        setForm((prev) => ({
          ...prev,
          first_name: result.first_name ?? "",
          last_name: result.last_name ?? "",
        }));
        setPhase("ready");
      } catch {
        if (active) setPhase("invalid");
        return;
      }
      // The country list is optional reference data — a geo outage must NOT invalidate a valid invite (country
      // is optional; the region field falls back to free text). Load it separately and tolerate failure.
      try {
        const countryList = await passportApi.geo.countries();
        if (active) setCountries(countryList);
      } catch {
        /* leave countries empty — the form stays usable */
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  function update<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCountryChange(iso2: string) {
    setForm((prev) => ({ ...prev, country: iso2, region: "" }));
    setStates([]);
    latestCountry.current = iso2;
    try {
      const st = await passportApi.geo.states(iso2);
      if (latestCountry.current === iso2) setStates(st);
    } catch {
      if (latestCountry.current === iso2) setStates([]);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    // first/last are the canonical name and required (server derives display_name from them).
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError("First and last name are required.");
      return;
    }
    setIsSubmitting(true);
    try {
      await passportApi.auth.acceptInvite({
        token: token!,
        password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        address_line1: clean(form.address_line1),
        address_line2: clean(form.address_line2),
        city: clean(form.city),
        region: clean(form.region),
        postal_code: clean(form.postal_code),
        country: clean(form.country),
      });
      toast.success("Welcome! Your account is ready.");
      // Hard navigation (full reload) so AuthProvider re-mounts and re-hydrates from the session cookie that
      // acceptInvite just set — an SPA navigate would hit AuthGuard against stale in-memory auth and bounce to /login.
      window.location.assign("/account");
    } catch {
      setError("This invite link is invalid or has expired.");
      toast.error("Could not accept the invite.");
      setIsSubmitting(false);
    }
  }

  if (phase === "validating") {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <p className="text-muted-foreground">Validating your invite…</p>
      </main>
    );
  }

  if (phase === "invalid") {
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-semibold">Invalid invite link</CardTitle>
              <CardDescription>
                This invitation link is missing, invalid, expired, or already used.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to="/login"
                className="text-sm text-foreground underline-offset-4 hover:underline"
              >
                Back to sign in
              </Link>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const countryOptions: ComboboxOption[] = countries.map((c) => ({
    value: c.iso2,
    label: c.name,
  }));
  const stateOptions: ComboboxOption[] = states.map((s) => ({
    value: s.code ?? s.name,
    label: s.name,
  }));

  return (
    <main className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold">Accept your invitation</CardTitle>
            <CardDescription>Set a password and complete your profile.</CardDescription>
          </CardHeader>
          <CardForm onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Email</p>
                <p className="text-sm">{email}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="first_name">First name</Label>
                  <Input id="first_name" required maxLength={100} value={form.first_name} onChange={(e) => update("first_name", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="last_name">Last name</Label>
                  <Input id="last_name" required maxLength={100} value={form.last_name} onChange={(e) => update("last_name", e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="confirm">Confirm</Label>
                  <Input id="confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="address_line1">Address line 1</Label>
                <Input id="address_line1" maxLength={200} value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="address_line2">Address line 2</Label>
                <Input id="address_line2" maxLength={200} value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} />
              </div>

              <div className="space-y-1">
                <Label htmlFor="country">Country</Label>
                <Combobox
                  id="country"
                  options={countryOptions}
                  value={form.country}
                  onChange={handleCountryChange}
                  placeholder="Select a country"
                  searchPlaceholder="Search countries…"
                  emptyText="No country found."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="region">State / Region</Label>
                  {stateOptions.length > 0 ? (
                    <Combobox
                      id="region"
                      options={stateOptions}
                      value={form.region}
                      onChange={(v) => update("region", v)}
                      placeholder="Select"
                      searchPlaceholder="Search…"
                      emptyText="No match."
                    />
                  ) : (
                    <Input id="region" maxLength={100} value={form.region} onChange={(e) => update("region", e.target.value)} />
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="city">City</Label>
                  <Input id="city" maxLength={100} value={form.city} onChange={(e) => update("city", e.target.value)} />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="postal_code">Postal code</Label>
                <Input id="postal_code" maxLength={20} value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} />
              </div>
            </CardContent>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Setting up…" : "Accept invitation"}
              </Button>
            </CardFooter>
          </CardForm>
        </Card>
      </div>
    </main>
  );
}
